/**
 * BursaryEligibilityQuiz — Wave 3 Money Layer · Task 3 (Feb 2026).
 *
 * A 4-question mini-quiz that answers the question every prospective
 * student silently asks looking at £10,050/yr fees: "how much aid could
 * I ACTUALLY get here?". Runs against `/api/money/bursary-quiz` which
 * scores answers against the curated `bursaries_registry`.
 *
 * Mounted inside `MoneyLayerCard` under the scholarships block.
 *
 * Design posture:
 *  - No account required (server does not persist answers).
 *  - Zero PII fields — household-income is a coarse dropdown band,
 *    not a text box. Anonymous by construction.
 *  - Honest transparency: we show the ineligible list too, so
 *    students see WHY a bursary didn't light up — protects trust and
 *    surfaces the "if I lived elsewhere / gained care-leaver status
 *    it would unlock" nuance.
 */
import { useEffect, useState } from "react";
import {
  PoundSterling, ChevronRight, ChevronLeft, ExternalLink,
  CheckCircle2, XCircle, Sparkles, Info,
} from "lucide-react";
import { api } from "@/lib/api";

// Household-income bands — chosen to align with UK gov student-finance
// tiers so answers map cleanly onto uni thresholds (£25k / £35k /
// £42,875 / £62k). Midpoint used server-side.
const INCOME_BANDS = [
  { key: "≤25k",       label: "£25,000 or less",     midpoint: 20000 },
  { key: "25-35k",     label: "£25,001 – £35,000",   midpoint: 30000 },
  { key: "35-43k",     label: "£35,001 – £42,875",   midpoint: 39000 },
  { key: "43-62k",     label: "£42,876 – £62,215",   midpoint: 55000 },
  { key: "62-75k",     label: "£62,216 – £75,000",   midpoint: 68000 },
  { key: ">75k",       label: "Over £75,000",        midpoint: 90000 },
  { key: "prefer",     label: "Prefer not to say",   midpoint: null  },
];

// Region matters for Scotland-domiciled awards (Edinburgh Access
// Bursary, Glasgow Talent Scholarship, etc.). Kept as a coarse pick.
const REGIONS = [
  "England", "Scotland", "Wales", "Northern Ireland",
  "Not UK / international",
];

export default function BursaryEligibilityQuiz({ degree, uni }) {
  const uniId = uni?.id || degree?.uni_id;
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    incomeBand: null,
    region: null,
    careLeaver: null,   // null = unanswered, true/false = answered
    contextFlags: [],   // subset of ["first_gen","estranged","disability"]
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasData, setHasData] = useState(null);  // null = unknown yet

  // Probe /money/bursaries for THIS uni before rendering — silence the
  // whole card when the registry has no rows for the uni. Avoids
  // surfacing "You qualify for £0" false-negatives on non-curated unis.
  useEffect(() => {
    if (!uniId) { setHasData(false); return; }
    api.get(`/money/bursaries?uni_id=${encodeURIComponent(uniId)}`)
      .then((r) => setHasData((r.data?.count || 0) > 0))
      .catch(() => setHasData(false));
  }, [uniId]);

  if (!uniId || hasData === false) return null;
  if (hasData === null) return null;   // hydrating

  const submit = async () => {
    setLoading(true); setError(null);
    try {
      const band = INCOME_BANDS.find((b) => b.key === answers.incomeBand);
      const region = answers.region;
      const homeDom = region == null
        ? null
        : region !== "Not UK / international";
      const payload = {
        uni_id: uniId,
        household_income: band?.midpoint ?? null,
        home_domicile: homeDom,
        region: region && region !== "Not UK / international" ? region : null,
        care_leaver: !!answers.careLeaver,
        first_gen:   answers.contextFlags.includes("first_gen"),
        estranged:   answers.contextFlags.includes("estranged"),
        disability:  answers.contextFlags.includes("disability"),
      };
      const { data } = await api.post("/money/bursary-quiz", payload);
      setResult(data);
      setStep(4);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not run the quiz — please retry.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setResult(null); setStep(0); setError(null); };

  // ── Step content ────────────────────────────────────────────────
  return (
    <div
      data-testid="bursary-eligibility-quiz"
      className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/40 p-5"
    >
      <div className="flex items-center gap-2">
        <PoundSterling className="h-4 w-4 text-emerald-700" aria-hidden />
        <h3 className="font-editorial text-xl text-[#002147]">
          How much bursary could you get here?
        </h3>
      </div>
      <p className="mt-1 text-[13px] text-stone-600">
        {result
          ? "Your results — every award below is auto-awarded by the university, not competitive."
          : "4 quick questions. No sign-in, no data stored. Scored against this university's published 2025-26 bursary policy."}
      </p>

      {/* Step indicator */}
      {!result && (
        <div className="mt-3 flex items-center gap-1.5">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              data-testid={`bursary-step-dot-${i}`}
              className={
                "h-1.5 flex-1 rounded-full " +
                (i <= step ? "bg-emerald-600" : "bg-stone-200")
              }
            />
          ))}
        </div>
      )}

      {/* STEP 0 · Household income */}
      {step === 0 && !result && (
        <QuestionShell
          testid="bursary-q-income"
          title="What's your household's total annual income (before tax)?"
          hint="Best guess is fine — we only use the band, not the exact figure."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {INCOME_BANDS.map((b) => (
              <button
                key={b.key}
                type="button"
                data-testid={`bursary-income-${b.key}`}
                onClick={() => {
                  setAnswers((a) => ({ ...a, incomeBand: b.key }));
                }}
                className={
                  "text-left rounded-md border px-3 py-2 text-[13px] transition " +
                  (answers.incomeBand === b.key
                    ? "border-emerald-600 bg-emerald-100 text-emerald-900"
                    : "border-stone-300 bg-white text-stone-700 hover:border-emerald-500")
                }
              >
                {b.label}
              </button>
            ))}
          </div>
        </QuestionShell>
      )}

      {/* STEP 1 · Region */}
      {step === 1 && !result && (
        <QuestionShell
          testid="bursary-q-region"
          title="Where do you live now?"
          hint="Some bursaries are only for students domiciled in a specific UK nation."
        >
          <div className="flex flex-wrap gap-1.5">
            {REGIONS.map((r) => (
              <button
                key={r}
                type="button"
                data-testid={`bursary-region-${r.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setAnswers((a) => ({ ...a, region: r }))}
                className={
                  "text-[12px] rounded-full border px-3 py-1 transition " +
                  (answers.region === r
                    ? "border-emerald-600 bg-emerald-100 text-emerald-900"
                    : "border-stone-300 bg-white text-stone-700 hover:border-emerald-500")
                }
              >
                {r}
              </button>
            ))}
          </div>
        </QuestionShell>
      )}

      {/* STEP 2 · Care-experienced */}
      {step === 2 && !result && (
        <QuestionShell
          testid="bursary-q-care"
          title="Are you a care leaver (spent time in local-authority care)?"
          hint="Nearly every UK uni now guarantees a top-tier bursary to care-experienced students, regardless of income."
        >
          <div className="flex gap-2">
            {[
              { v: true,  label: "Yes" },
              { v: false, label: "No" },
            ].map((opt) => (
              <button
                key={String(opt.v)}
                type="button"
                data-testid={`bursary-care-${opt.v ? "yes" : "no"}`}
                onClick={() =>
                  setAnswers((a) => ({ ...a, careLeaver: opt.v }))
                }
                className={
                  "flex-1 rounded-md border px-3 py-2 text-[13px] transition " +
                  (answers.careLeaver === opt.v
                    ? "border-emerald-600 bg-emerald-100 text-emerald-900"
                    : "border-stone-300 bg-white text-stone-700 hover:border-emerald-500")
                }
              >
                {opt.label}
              </button>
            ))}
          </div>
        </QuestionShell>
      )}

      {/* STEP 3 · Context flags (multi-select) */}
      {step === 3 && !result && (
        <QuestionShell
          testid="bursary-q-context"
          title="Any of these apply? (tick all that fit)"
          hint="Some awards top-up for students in these groups. Skip if none apply."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {[
              { key: "first_gen",  label: "First in family to go to university" },
              { key: "estranged",  label: "Estranged from parents" },
              { key: "disability", label: "Registered disability" },
            ].map((f) => {
              const on = answers.contextFlags.includes(f.key);
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`bursary-flag-${f.key}`}
                  onClick={() =>
                    setAnswers((a) => ({
                      ...a,
                      contextFlags: on
                        ? a.contextFlags.filter((x) => x !== f.key)
                        : [...a.contextFlags, f.key],
                    }))
                  }
                  className={
                    "flex items-center gap-2 rounded-md border px-3 py-2 text-left text-[13px] transition " +
                    (on
                      ? "border-emerald-600 bg-emerald-100 text-emerald-900"
                      : "border-stone-300 bg-white text-stone-700 hover:border-emerald-500")
                  }
                >
                  {on
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" aria-hidden />
                    : <span className="h-4 w-4 rounded-full border border-stone-400 shrink-0" />
                  }
                  <span>{f.label}</span>
                </button>
              );
            })}
          </div>
        </QuestionShell>
      )}

      {/* Nav buttons (question flow) */}
      {!result && (
        <div className="mt-4 flex items-center justify-between">
          <button
            type="button"
            data-testid="bursary-back"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="inline-flex items-center gap-1 text-[12px] text-stone-600 hover:text-stone-900 disabled:opacity-40"
          >
            <ChevronLeft className="h-3 w-3" /> Back
          </button>
          {step < 3 ? (
            <button
              type="button"
              data-testid="bursary-next"
              disabled={
                (step === 0 && !answers.incomeBand) ||
                (step === 1 && !answers.region) ||
                (step === 2 && answers.careLeaver === null)
              }
              onClick={() => setStep((s) => s + 1)}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-700 text-white text-[13px] font-medium px-3 py-1.5 hover:bg-emerald-800 disabled:opacity-50"
            >
              Next <ChevronRight className="h-3 w-3" />
            </button>
          ) : (
            <button
              type="button"
              data-testid="bursary-submit"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-700 text-white text-[13px] font-medium px-3 py-1.5 hover:bg-emerald-800 disabled:opacity-50"
            >
              {loading ? "Scoring…" : (
                <>See my results <Sparkles className="h-3 w-3" /></>
              )}
            </button>
          )}
        </div>
      )}

      {error && (
        <div
          data-testid="bursary-error"
          className="mt-2 text-[12px] text-rose-700"
        >
          {error}
        </div>
      )}

      {/* RESULT VIEW */}
      {result && (
        <BursaryResults result={result} onReset={reset} />
      )}
    </div>
  );
}


function QuestionShell({ testid, title, hint, children }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <div className="text-[13px] font-medium text-[#002147]">{title}</div>
      {hint && (
        <div className="mt-0.5 flex items-start gap-1 text-[11px] text-stone-500">
          <Info className="h-3 w-3 shrink-0 mt-0.5" aria-hidden />
          <span>{hint}</span>
        </div>
      )}
      <div className="mt-2">{children}</div>
    </div>
  );
}


function BursaryResults({ result, onReset }) {
  const { eligible, ineligible, total_annual_gbp, total_over_course_gbp } = result;
  const [showIneligible, setShowIneligible] = useState(false);

  return (
    <div className="mt-4 space-y-3" data-testid="bursary-results">
      {eligible.length > 0 ? (
        <div
          data-testid="bursary-total-headline"
          className="rounded-lg border border-emerald-400 bg-white p-3"
        >
          <div className="text-[11px] font-underground-sc tracking-wider text-emerald-700">
            Estimated bursary aid
          </div>
          <div className="mt-0.5 flex items-baseline gap-3 flex-wrap">
            <span className="font-editorial text-3xl text-emerald-900 tabular-nums">
              £{total_annual_gbp.toLocaleString()}
            </span>
            <span className="text-[12px] text-stone-600">per year</span>
            <span className="text-stone-300">·</span>
            <span className="font-editorial text-lg text-stone-700 tabular-nums">
              £{total_over_course_gbp.toLocaleString()}
            </span>
            <span className="text-[12px] text-stone-600">over your course</span>
          </div>
          <p className="mt-1 text-[11px] text-stone-500">
            Non-repayable, awarded automatically on enrolment. Confirm the
            exact amount with each linked university page — some awards
            are means-tested on a sliding scale within the band we scored.
          </p>
        </div>
      ) : (
        <div
          data-testid="bursary-no-eligible"
          className="rounded-lg border border-stone-300 bg-white p-3 text-[13px] text-stone-700"
        >
          Based on your answers, you're unlikely to qualify for a
          needs-based bursary at this uni. Check the "why not" list
          below — some criteria (region, care-leaver status) can flip a
          result unexpectedly. You may still qualify for
          merit-based scholarships listed above.
        </div>
      )}

      {/* Eligible list */}
      {eligible.length > 0 && (
        <ul className="space-y-2" data-testid="bursary-eligible-list">
          {eligible.map((b) => (
            <li
              key={b.id}
              data-testid={`bursary-eligible-${b.id}`}
              className="rounded-lg border border-emerald-300 bg-white p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-editorial text-[15px] text-[#002147]">
                    {b.name}
                  </div>
                  <div className="mt-0.5 text-[12px] text-stone-600">
                    <span className="font-medium text-emerald-800">
                      {b.amount_display}
                    </span>
                    <span className="text-stone-400"> · </span>
                    <span>Awarded for {b.duration_years} yr{b.duration_years === 1 ? "" : "s"}</span>
                  </div>
                  {b.match_highlights?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {b.match_highlights.map((h) => (
                        <span
                          key={h}
                          className="text-[10px] font-mono rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 px-1.5 py-0.5"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-1 text-[11px] text-stone-500">
                    Source: {b.cite}
                  </div>
                </div>
                <a
                  href={b.apply_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`bursary-apply-${b.id}`}
                  className="shrink-0 text-emerald-800 hover:underline inline-flex items-center gap-0.5 text-[12px] font-medium"
                >
                  Details <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Ineligible list · collapsible */}
      {ineligible.length > 0 && (
        <div>
          <button
            type="button"
            data-testid="bursary-toggle-ineligible"
            onClick={() => setShowIneligible((x) => !x)}
            className="text-[12px] text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
          >
            {showIneligible ? "Hide" : "Show"} why {ineligible.length} other{" "}
            bursar{ineligible.length === 1 ? "y" : "ies"} didn't qualify
          </button>
          {showIneligible && (
            <ul className="mt-2 space-y-1.5" data-testid="bursary-ineligible-list">
              {ineligible.map((b) => (
                <li
                  key={b.id}
                  data-testid={`bursary-ineligible-${b.id}`}
                  className="rounded-md border border-stone-200 bg-stone-50 p-2 text-[12px]"
                >
                  <div className="flex items-start gap-2">
                    <XCircle className="h-3.5 w-3.5 text-stone-400 shrink-0 mt-0.5" aria-hidden />
                    <div>
                      <span className="text-stone-800 font-medium">{b.name}</span>
                      <span className="text-stone-500"> · {b.amount_display}</span>
                      <div className="text-[11px] text-stone-600">
                        {(b.ineligibility_reasons || []).join(" ")}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <button
        type="button"
        data-testid="bursary-reset"
        onClick={onReset}
        className="mt-2 text-[12px] text-emerald-800 hover:underline"
      >
        Redo the quiz with different answers
      </button>
    </div>
  );
}
